import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, LANGUAGES } from '@/i18n/LanguageContext';
import { t } from '@/i18n/translations';

const NavLogo = ({ isHome }) => (
  <Link to="/" className="flex items-center" data-testid="logo-link">
    {isHome && <img src="/logo_compact.png" alt="Homewise" className="h-12 w-auto lg:hidden" />}
    {!isHome && <img src="/logo_navbar.jpg" alt="Homewise" className="h-14 w-auto lg:hidden" />}
    <img src="/logo_navbar.jpg" alt="Homewise" className="h-14 w-auto hidden lg:block" />
  </Link>
);

const LangDropdown = ({ lang, setLang, langOpen, setLangOpen, testIdSuffix = '' }) => (
  <div className="relative">
    <button
      onClick={() => setLangOpen(!langOpen)}
      className={`flex items-center transition-colors ${testIdSuffix ? 'text-white' : 'text-white hover:text-[#3e716c]'}`}
      data-testid={`lang-selector${testIdSuffix}`}
    >
      <Globe size={18} className="mr-1" />
      <span className="text-sm font-medium">{lang.toUpperCase()}</span>
    </button>
    {langOpen && (
      <div className="absolute right-0 top-8 bg-white rounded-sm shadow-lg border border-[#E2E8F0] overflow-hidden min-w-[120px] z-50">
        {LANGUAGES.map((l) => (
          <button
            key={l.code}
            onClick={() => { setLang(l.code); setLangOpen(false); }}
            data-testid={`lang-${l.code}`}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-[#F7F9F9] transition-colors ${
              lang === l.code ? 'text-[#3e716c] font-semibold bg-[#F7F9F9]' : 'text-[#0A1312]'
            }`}
          >
            {l.name}
          </button>
        ))}
      </div>
    )}
  </div>
);

const DesktopNav = ({ navLinks, isActive, nav, lang, setLang, langOpen, setLangOpen }) => (
  <div className="hidden lg:flex items-center space-x-5">
    {navLinks.map((link) => (
      <Link
        key={link.path}
        to={link.path}
        data-testid={`nav-${link.path.slice(1) || 'home'}`}
        className={`text-sm font-medium transition-colors whitespace-nowrap ${
          isActive(link.path) ? 'text-[#3e716c]' : 'text-white hover:text-[#3e716c]'
        }`}
      >
        {link.name}
      </Link>
    ))}
    <Link to="/valutazione-gratuita">
      <Button data-testid="cta-button-nav" className="bg-white text-[#3e716c] hover:bg-[#F7F9F9] rounded-sm px-5 font-semibold whitespace-nowrap">
        {nav.cta}
      </Button>
    </Link>
    <LangDropdown lang={lang} setLang={setLang} langOpen={langOpen} setLangOpen={setLangOpen} />
  </div>
);

const MobileMenu = ({ navLinks, isActive, nav, onClose }) => (
  <div className="lg:hidden bg-white absolute top-20 left-0 right-0 z-50 shadow-lg border-t border-[#E2E8F0]" data-testid="mobile-menu">
    <div className="px-4 pt-2 pb-4 space-y-1">
      {navLinks.map((link) => (
        <Link
          key={link.path}
          to={link.path}
          onClick={onClose}
          data-testid={`mobile-nav-${link.path.slice(1) || 'home'}`}
          className={`block py-3 px-2 text-base font-medium rounded-sm ${
            isActive(link.path) ? 'text-[#3e716c] bg-[#F7F9F9]' : 'text-[#0A1312] hover:bg-[#F7F9F9]'
          }`}
        >
          {link.name}
        </Link>
      ))}
      <Link to="/valutazione-gratuita" onClick={onClose} className="block pt-2">
        <Button className="w-full bg-[#65c3ba] text-white hover:bg-[#3e716c] rounded-sm">{nav.cta}</Button>
      </Link>
    </div>
  </div>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { lang, setLang } = useLanguage();
  const nav = t('nav', lang);
  const isHome = location.pathname === '/';

  const navLinks = [
    { name: nav.home, path: '/' },
    { name: nav.chiSiamo, path: '/chi-siamo' },
    { name: nav.cosaOffriamo, path: '/cosa-offriamo' },
    { name: nav.comeFunziona, path: '/come-funziona' },
    { name: nav.proprietario, path: '/sei-un-proprietario' },
    { name: nav.strutture, path: '/strutture' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 w-full z-50 bg-[#65c3ba] shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <NavLogo isHome={isHome} />
          <DesktopNav navLinks={navLinks} isActive={isActive} nav={nav} lang={lang} setLang={setLang} langOpen={langOpen} setLangOpen={setLangOpen} />
          <div className="lg:hidden flex items-center space-x-3">
            <LangDropdown lang={lang} setLang={setLang} langOpen={langOpen} setLangOpen={setLangOpen} testIdSuffix="-mobile" />
            <button data-testid="mobile-menu-button" onClick={() => setIsOpen(!isOpen)} className="text-white">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>
      {isOpen && <MobileMenu navLinks={navLinks} isActive={isActive} nav={nav} onClose={() => setIsOpen(false)} />}
    </nav>
  );
};

export default Navbar;
